import { useEffect, useRef, useState } from 'react';

interface FormErrors {
  name?: string;
  email?: string;
  service?: string;
  message?: string;
}

export default function Contact() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    objectif: [] as string[],
    message: '',
    source: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Ce champ est obligatoire.';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Ce champ est obligatoire.';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide.';
      isValid = false;
    }

    if (!formData.service.trim()) {
      newErrors.service = 'Veuillez sélectionner un service.';
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Ce champ est obligatoire.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleObjectif = (value: string) => {
    setFormData((prev) => {
      const isSelected = prev.objectif.includes(value);
      if (isSelected) {
        return { ...prev, objectif: prev.objectif.filter((o) => o !== value) };
      }
      return { ...prev, objectif: [...prev.objectif, value] };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) return;

    const form = e.currentTarget;

    const supabaseUrl = 'https://npudzsgfeuxlfvkyrikf.supabase.co';
    const anonKey = 'sb_publishable_KSTYLXI7_uL65Xps6Cnnrg_Z02qI180';

    const payload = {
      name: formData.name,
      email: formData.email,
      service: formData.service,
      objectif: formData.objectif.join(', '),
      message: formData.message,
      source: formData.source,
    };

    fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Erreur serveur');
        return res.json();
      })
      .then(() => {
        setSubmitted(true);
        setFormData({ name: '', email: '', service: '', objectif: [], message: '', source: '' });
        form.reset();
      })
      .catch(() => {
        setErrors({ message: "Une erreur est survenue. Veuillez réessayer." });
      });
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand mb-4 block">
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium text-white mb-4">
            Parlons de votre projet
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-base md:text-lg font-light">
            Racontez-moi vos idées, je vous propose la meilleure solution
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div
            className={`lg:col-span-12 transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {submitted ? (
              <div className="glass-card rounded-2xl p-10 text-center animate-fadeIn">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand/10 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-brand"></i>
                </div>
                <h3 className="text-xl font-display font-medium text-white mb-3">
                  Merci pour votre confiance.
                </h3>
                <p className="text-sm text-white/60 font-light leading-relaxed">
                  Votre demande a bien été envoyée. Je reviens vers vous rapidement afin d'échanger sur votre projet.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setErrors({});
                  }}
                  className="mt-6 text-sm text-brand hover:text-brand-light transition-colors"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="glass-card rounded-2xl p-8 space-y-5 max-w-5xl mx-auto"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                      Nom <span className="text-brand">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      className={`w-full px-4 py-3 rounded-xl bg-dark-surface border text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand/50 transition-colors ${
                        errors.name ? 'border-red-400/50' : 'border-white/10'
                      }`}
                      placeholder="Votre nom"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-400 mt-1.5 font-light">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                      E-mail <span className="text-brand">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      className={`w-full px-4 py-3 rounded-xl bg-dark-surface border text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand/50 transition-colors ${
                        errors.email ? 'border-red-400/50' : 'border-white/10'
                      }`}
                      placeholder="votre@email.ch"
                    />
                    {errors.email && (
                      <p className="text-xs text-red-400 mt-1.5 font-light">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-3">
                    Service recherché <span className="text-brand">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Création de site web',
                      'Création de logo',
                      'Supports de communication',
                      'Gestion des réseaux sociaux',
                      'Projet complet / identité globale',
                    ].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleInputChange('service', s)}
                        className={`px-4 py-2.5 rounded-lg text-sm border transition-all duration-300 whitespace-nowrap ${
                          formData.service === s
                            ? 'border-brand/50 bg-brand/10 text-brand'
                            : 'border-brand/20 text-white/60 hover:border-brand/40 hover:bg-brand/5 hover:text-white/80'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="service" value={formData.service} />
                  {errors.service && (
                    <p className="text-xs text-red-400 mt-2 font-light">
                      {errors.service}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-3">
                    Objectif principal
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Attirer plus de clients',
                      'Moderniser mon image',
                      'Développer ma visibilité',
                      'Créer une identité professionnelle',
                      'Lancer mon activité',
                    ].map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => toggleObjectif(o)}
                        className={`px-4 py-2.5 rounded-lg text-sm border transition-all duration-300 whitespace-nowrap ${
                          formData.objectif.includes(o)
                            ? 'border-brand/50 bg-brand/10 text-brand'
                            : 'border-brand/20 text-white/60 hover:border-brand/40 hover:bg-brand/5 hover:text-white/80'
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="objectif" value={formData.objectif.join(', ')} />
                </div>

                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                    Message <span className="text-brand">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    required
                    rows={5}
                    maxLength={500}
                    className={`w-full px-4 py-3 rounded-xl bg-dark-surface border text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand/50 transition-colors resize-none ${
                      errors.message ? 'border-red-400/50' : 'border-white/10'
                    }`}
                    placeholder="Décrivez votre projet, vos idées ou vos besoins…"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-white/30">Maximum 500 caractères</p>
                    {errors.message && (
                      <p className="text-xs text-red-400 font-light">
                        {errors.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                    Comment m'avez-vous trouvé ?
                  </label>
                  <div className="relative">
                    <select
                      name="source"
                      value={formData.source}
                      onChange={(e) => handleInputChange('source', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-dark-surface border border-white/10 text-white text-sm focus:outline-none focus:border-brand/50 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" disabled className="bg-dark-surface text-white/40">
                        Sélectionnez une option
                      </option>
                      <option value="Instagram" className="bg-dark-surface text-white">Instagram</option>
                      <option value="TikTok" className="bg-dark-surface text-white">TikTok</option>
                      <option value="Google" className="bg-dark-surface text-white">Google</option>
                      <option value="Recommandation" className="bg-dark-surface text-white">Recommandation</option>
                      <option value="Autre" className="bg-dark-surface text-white">Autre</option>
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-brand">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-lg"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full px-8 py-4 text-sm whitespace-nowrap"
                >
                  Discutons de votre projet
                </button>

                <p className="text-center text-xs text-white/30 font-light">
                  Réponse rapide garantie
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
